import { useState, useEffect, useMemo, useRef } from "react";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";

interface Props {
  onClose: () => void;
  onSave: (data: {
    leadId?: string | number;
    leadName: string;
    phone: string;
    agent: string;
    date: string;
    time: string;
    duration: string;
    durationSeconds: number;
    status: string;
    purpose: string;
    notes: string;
    outcome: string;
  }) => void;
}

const statusOptions = ["Connected", "Missed", "Busy", "No Answer", "Voicemail"];
const purposeOptions = ["Initial Contact", "Follow-up", "Demo", "Negotiation", "Closing", "Support"];
const outcomeOptions = ["Interested", "Not Interested", "Call Back Later", "Site Visit Scheduled", "Proposal Requested", "Converted"];

export default function LogCallModal({ onClose, onSave }: Props) {
  const { showToast } = useToast();

  const [leadSearch, setLeadSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [selectedLead, setSelectedLead] = useState<{ id?: string | number; name: string; phone: string; company?: string } | null>(null);
  const [direction, setDirection] = useState<"Outbound" | "Inbound">("Outbound");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [timeStr, setTimeStr] = useState(
    new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })
  );
  const [durationMin, setDurationMin] = useState("0");
  const [durationSec, setDurationSec] = useState("0");
  const [status, setStatus] = useState("Connected");
  const [purpose, setPurpose] = useState("Follow-up");
  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState("");
  const [assigned, setAssigned] = useState("Agent");
  const [leadsList, setLeadsList] = useState<{ id: string | number; name: string; phone: string; company?: string }[]>([]);
  const [agentsList, setAgentsList] = useState<string[]>([]);

  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownContainerRef.current && !dropdownContainerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Initial load: fetch leads and agents
  useEffect(() => {
    setLoadingLeads(true);
    fetchApi<any>("/leads?limit=100", undefined, null)
      .then((res) => {
        const rawList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];
        setLeadsList(
          rawList.map((l: any) => ({
            id: l.id,
            name: l.name || l.contact_name || "Lead",
            phone: l.phone || l.mobile || "",
            company: l.company || "",
          }))
        );
      })
      .catch((err) => {
        console.warn("Could not load leads in LogCallModal", err);
      })
      .finally(() => {
        setLoadingLeads(false);
      });

    fetchApi<any>("/auth/company-user/all", undefined, [])
      .then((res) => {
        const userList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data)
          ? res.data
          : [];
        const names = userList.map((u: any) => u.name || u.fullName || u.username).filter(Boolean);
        if (names.length > 0) {
          setAgentsList(Array.from(new Set(names)));
          setAssigned(names[0]);
        }
      })
      .catch(() => {});
  }, []);

  // Backend search debounce if typed query has >= 2 characters
  useEffect(() => {
    if (!leadSearch.trim() || selectedLead) return;
    const timer = setTimeout(() => {
      fetchApi<any>(`/leads?limit=50&search=${encodeURIComponent(leadSearch.trim())}`, undefined, null)
        .then((res) => {
          const rawList = Array.isArray(res)
            ? res
            : Array.isArray(res?.items)
            ? res.items
            : Array.isArray(res?.data?.items)
            ? res.data.items
            : Array.isArray(res?.data)
            ? res.data
            : [];
          if (rawList.length > 0) {
            setLeadsList((prev) => {
              const existingIds = new Set(prev.map((p) => p.id));
              const newItems = rawList
                .filter((l: any) => !existingIds.has(l.id))
                .map((l: any) => ({
                  id: l.id,
                  name: l.name || l.contact_name || "Lead",
                  phone: l.phone || l.mobile || "",
                  company: l.company || "",
                }));
              return [...prev, ...newItems];
            });
          }
        })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [leadSearch, selectedLead]);

  const filteredLeads = useMemo(() => {
    const query = leadSearch.trim().toLowerCase();
    if (!query) return leadsList.slice(0, 10);
    return leadsList
      .filter(
        (l) =>
          l.name.toLowerCase().includes(query) ||
          l.phone.toLowerCase().includes(query) ||
          (l.company && l.company.toLowerCase().includes(query))
      )
      .slice(0, 10);
  }, [leadsList, leadSearch]);

  const handleSelectLead = (lead: { id?: string | number; name: string; phone: string; company?: string }) => {
    setSelectedLead(lead);
    setLeadSearch(lead.name);
    setShowDropdown(false);
  };

  const handleSave = () => {
    if (!selectedLead) {
      showToast("Please select a lead", "error");
      return;
    }
    const mins = parseInt(durationMin) || 0;
    const secs = parseInt(durationSec) || 0;
    const totalSecs = mins * 60 + secs;
    const durStr = secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;

    onSave({
      leadId: selectedLead.id,
      leadName: selectedLead.name,
      phone: selectedLead.phone,
      agent: assigned,
      date: `Today ${timeStr}`,
      time: timeStr,
      duration: durStr,
      durationSeconds: totalSecs,
      status,
      purpose,
      notes,
      outcome,
    });
    showToast("Call logged successfully", "success");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl w-full max-w-md shadow-lg animate-slide-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-[#111827]">Log a Call</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Lead Search */}
          <div className="relative" ref={dropdownContainerRef}>
            <label className="block text-xs font-semibold text-[#374151] mb-1">
              Lead <span className="text-red-500">*</span>
            </label>
            {selectedLead ? (
              <div className="flex items-center gap-2.5 px-3 py-2 border border-indigo-200 bg-indigo-50/60 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-xs shrink-0">
                  {selectedLead.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#111827] truncate">{selectedLead.name}</p>
                  <p className="text-xs text-[#6b7280] truncate">
                    {selectedLead.company ? `${selectedLead.company} · ` : ""}
                    {selectedLead.phone || "No phone"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedLead(null);
                    setLeadSearch("");
                    setShowDropdown(true);
                  }}
                  className="text-gray-400 hover:text-red-500 p-1 transition-colors"
                  title="Change lead"
                >
                  <i className="ri-close-circle-line text-lg" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="relative flex items-center">
                  <i className="ri-search-line absolute left-3 text-gray-400 text-sm pointer-events-none" />
                  <input
                    type="text"
                    value={leadSearch}
                    onChange={(e) => {
                      setLeadSearch(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Search lead by name, phone, or company..."
                    className="w-full border border-gray-200 rounded-lg pl-9 pr-8 py-2 text-sm text-[#111827] placeholder-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                  />
                  {leadSearch && (
                    <button
                      type="button"
                      onClick={() => {
                        setLeadSearch("");
                        setShowDropdown(true);
                      }}
                      className="absolute right-2.5 text-gray-400 hover:text-gray-600"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  )}
                </div>

                {/* Dropdown Menu */}
                {showDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-56 overflow-y-auto divide-y divide-gray-100">
                    {loadingLeads && leadsList.length === 0 ? (
                      <div className="px-4 py-3 text-xs text-gray-500 flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin text-indigo-600 text-sm" />
                        <span>Loading leads...</span>
                      </div>
                    ) : filteredLeads.length > 0 ? (
                      filteredLeads.map((l) => (
                        <button
                          key={l.id}
                          type="button"
                          onClick={() => handleSelectLead(l)}
                          className="w-full text-left px-3 py-2.5 hover:bg-indigo-50/50 flex items-center gap-2.5 transition-colors cursor-pointer group"
                        >
                          <div className="w-7 h-7 rounded-full bg-indigo-100 group-hover:bg-indigo-200 flex items-center justify-center flex-shrink-0 transition-colors">
                            <span className="text-[10px] font-bold text-indigo-600">
                              {l.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .slice(0, 2)
                                .toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-[#111827] truncate group-hover:text-indigo-600">
                              {l.name}
                            </p>
                            <p className="text-xs text-[#6b7280] truncate">
                              {l.company ? `${l.company} · ` : ""}
                              {l.phone ? (
                                <span className="inline-flex items-center gap-1">
                                  <i className="ri-phone-line text-[10px]" /> {l.phone}
                                </span>
                              ) : (
                                "No phone"
                              )}
                            </p>
                          </div>
                          <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-indigo-500 text-sm" />
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-4 text-center text-xs text-gray-500">
                        <i className="ri-user-search-line text-lg text-gray-400 block mb-1" />
                        {leadSearch ? (
                          <span>No leads matching &quot;{leadSearch}&quot;</span>
                        ) : (
                          <span>No leads available</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Direction Toggle */}
          <div>
            <label className="block text-xs font-semibold text-[#374151] mb-1">Call Direction</label>
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              {(["Outbound", "Inbound"] as const).map((d) => (
                <button
                  key={d}
                  onClick={() => setDirection(d)}
                  className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                    direction === d
                      ? "bg-white text-[#111827] shadow-sm"
                      : "text-[#6b7280] hover:text-[#374151]"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#374151] mb-1">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#111827] focus:outline-none focus:ring-1 focus:ring-indigo-300"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#374151] mb-1">Time</label>
              <input
                type="time"
                value={timeStr}
                onChange={(e) => setTimeStr(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#111827] focus:outline-none focus:ring-1 focus:ring-indigo-300"
              />
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-xs font-semibold text-[#374151] mb-1">Duration</label>
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <input
                  type="number"
                  value={durationMin}
                  onChange={(e) => setDurationMin(e.target.value)}
                  placeholder="Min"
                  min="0"
                  className="w-full border border-gray-200 rounded-lg pl-3 pr-10 py-2 text-sm text-[#111827] focus:outline-none focus:ring-1 focus:ring-indigo-300"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#9ca3af]">min</span>
              </div>
              <div className="relative">
                <input
                  type="number"
                  value={durationSec}
                  onChange={(e) => setDurationSec(e.target.value)}
                  placeholder="Sec"
                  min="0"
                  max="59"
                  className="w-full border border-gray-200 rounded-lg pl-3 pr-10 py-2 text-sm text-[#111827] focus:outline-none focus:ring-1 focus:ring-indigo-300"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#9ca3af]">sec</span>
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-semibold text-[#374151] mb-1">Call Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#111827] focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Purpose */}
          <div>
            <label className="block text-xs font-semibold text-[#374151] mb-1">Purpose</label>
            <select
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#111827] focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              {purposeOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-[#374151] mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What was discussed..."
              className="w-full border border-gray-200 rounded-lg p-3 text-sm text-[#111827] placeholder-[#9ca3af] focus:outline-none focus:ring-1 focus:ring-indigo-300 resize-none"
              rows={3}
            />
          </div>

          {/* Outcome */}
          <div>
            <label className="block text-xs font-semibold text-[#374151] mb-1">Outcome</label>
            <select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#111827] focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              <option value="">Select outcome...</option>
              {outcomeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          {/* Agent */}
          <div>
            <label className="block text-xs font-semibold text-[#374151] mb-1">Assigned Agent</label>
            <select
              value={assigned}
              onChange={(e) => setAssigned(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#111827] focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              {agentsList.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-5 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium text-[#374151] rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
          >
            Save Call Log
          </button>
        </div>
      </div>
    </div>
  );
}